// src/views/reports/CollectionsReportView.js

import React, { useState, useEffect } from 'react';
import { db, functions } from '../../firebase/config';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Loader2, Send, CheckCircle, MailWarning, ArrowLeft, Download, X, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatCurrency } from '../../utils/formatters';
import { exportToCSV } from '../../utils/csvExport';
import { computeInvoiceBalance } from '../../utils/invoiceBalance';

// --- DRILL DOWN MODAL ---
const CollectionsDetailModal = ({ account, onClose, onSendReminder, sendingId }) => {
    // Seam 10 (part 2a): period-statement date range. Defaults to the last 12 months; the
    // operator can adjust it before sending. Hooks must run before the early return below.
    const [stmtStart, setStmtStart] = useState(() => {
        const d = new Date();
        d.setFullYear(d.getFullYear() - 1);
        return d.toISOString().split('T')[0];
    });
    const [stmtEnd, setStmtEnd] = useState(() => new Date().toISOString().split('T')[0]);

    if (!account) return null;

    const range = { startDate: stmtStart, endDate: stmtEnd };
    const overdueInvoices = account.invoices.filter(inv => inv.isOverdue);
    const currentInvoices = account.invoices.filter(inv => !inv.isOverdue);

    // --- NEW: EXPORT INDIVIDUAL CUSTOMER STATEMENT ---
    const handleExportStatement = () => {
        if (!account.invoices || account.invoices.length === 0) return;

        const csvData = account.invoices.map(inv => ({
            Invoice_Number: inv.invoiceNumber,
            Due_Date: inv.dueDate ? inv.dueDate.toLocaleDateString() : 'N/A',
            Status: inv.isOverdue ? 'OVERDUE' : 'Current',
            Total_Amount: inv.total.toFixed(2),
            Balance_Due: inv.balance.toFixed(2)
        }));

        const cleanName = account.name.replace(/[^a-z0-9]/gi, '_');
        exportToCSV(csvData, `Statement_${cleanName}_${new Date().toISOString().split('T')[0]}.csv`);
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b bg-gray-50">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">{account.name}</h2>
                        <p className="text-sm text-gray-500">Account Status Details</p>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* EXPORT BUTTON */}
                        <button 
                            onClick={handleExportStatement}
                            className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
                        >
                            <Download size={16} /> Export Statement
                        </button>
                        <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                            <X size={20} className="text-gray-500" />
                        </button>
                    </div>
                </div>

                {/* STATEMENT TOOLBAR (Seam 10, part 2a): pick a period and send the account
                    statement. Always available for the opened account, regardless of which
                    sections below render. */}
                <div className="px-6 py-3 border-b bg-white flex flex-wrap items-end gap-3">
                    <div>
                        <label className="block text-xs text-gray-500 mb-1">Statement from</label>
                        <input
                            type="date"
                            value={stmtStart}
                            onChange={(e) => setStmtStart(e.target.value)}
                            className="border border-gray-300 rounded px-2 py-1 text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-xs text-gray-500 mb-1">to</label>
                        <input
                            type="date"
                            value={stmtEnd}
                            onChange={(e) => setStmtEnd(e.target.value)}
                            className="border border-gray-300 rounded px-2 py-1 text-sm"
                        />
                    </div>
                    <button
                        onClick={() => onSendReminder(account, 'standard', range)}
                        disabled={sendingId === account.customerId}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 shadow-sm disabled:opacity-50"
                    >
                        {sendingId === account.customerId ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                        Send Statement
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6 space-y-8">

                    {/* OVERDUE SECTION */}
                    {overdueInvoices.length > 0 && (
                        <div>
                            <div className="flex justify-between items-center mb-3">
                                <h3 className="text-red-700 font-bold flex items-center gap-2">
                                    <AlertCircle size={20} /> Action Required (Overdue)
                                </h3>
                                <button 
                                    onClick={() => onSendReminder(account, 'overdue')}
                                    disabled={sendingId === account.customerId}
                                    className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 text-sm font-medium shadow-sm flex items-center gap-2 disabled:opacity-50"
                                >
                                    {sendingId === account.customerId ? <Loader2 className="animate-spin" size={16} /> : <MailWarning size={16} />}
                                    Send Overdue Reminder
                                </button>
                            </div>
                            <div className="border border-red-200 rounded-lg overflow-hidden">
                                <table className="w-full text-sm text-left">
                                    <thead className="bg-red-50 text-red-900 uppercase text-xs font-semibold">
                                        <tr>
                                            <th className="px-4 py-3">Invoice #</th>
                                            <th className="px-4 py-3">Due Date</th>
                                            <th className="px-4 py-3 text-right">Total</th>
                                            <th className="px-4 py-3 text-right">Balance Due</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-red-100">
                                        {overdueInvoices.map(inv => (
                                            <tr key={inv.id} className="bg-white">
                                                <td className="px-4 py-3 font-medium text-red-600">#{inv.invoiceNumber}</td>
                                                <td className="px-4 py-3">{inv.dueDate ? inv.dueDate.toLocaleDateString() : 'N/A'}</td>
                                                <td className="px-4 py-3 text-right text-gray-500">{formatCurrency(inv.total)}</td>
                                                <td className="px-4 py-3 text-right font-bold text-red-700">{formatCurrency(inv.balance)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* CURRENT SECTION */}
                    {currentInvoices.length > 0 && (
                        <div>
                            <div className="flex justify-between items-center mb-3">
                                <h3 className="text-gray-700 font-bold">Current Invoices (Coming Up)</h3>
                                <button
                                    onClick={() => onSendReminder(account, 'standard', range)}
                                    disabled={sendingId === account.customerId}
                                    className="border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 text-sm font-medium shadow-sm flex items-center gap-2 disabled:opacity-50"
                                >
                                    {sendingId === account.customerId ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
                                    Send Statement
                                </button>
                            </div>
                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                                <table className="w-full text-sm text-left">
                                    <thead className="bg-gray-50 text-gray-600 uppercase text-xs font-semibold">
                                        <tr>
                                            <th className="px-4 py-3">Invoice #</th>
                                            <th className="px-4 py-3">Due Date</th>
                                            <th className="px-4 py-3 text-right">Total</th>
                                            <th className="px-4 py-3 text-right">Balance Due</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {currentInvoices.map(inv => (
                                            <tr key={inv.id} className="bg-white">
                                                <td className="px-4 py-3 font-medium">#{inv.invoiceNumber}</td>
                                                <td className="px-4 py-3">{inv.dueDate ? inv.dueDate.toLocaleDateString() : 'N/A'}</td>
                                                <td className="px-4 py-3 text-right text-gray-500">{formatCurrency(inv.total)}</td>
                                                <td className="px-4 py-3 text-right font-medium">{formatCurrency(inv.balance)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t bg-gray-50 flex justify-end">
                    <button onClick={onClose} className="px-6 py-2 bg-white border border-gray-300 rounded text-gray-700 hover:bg-gray-50 font-medium">
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- MAIN VIEW ---
const CollectionsReportView = ({ currentUserProfile }) => {
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sendingId, setSendingId] = useState(null);
    
    // Modal State
    const [selectedAccount, setSelectedAccount] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            if (!currentUserProfile?.organizationId) return;
            const orgId = currentUserProfile.organizationId;

            try {
                // 1. Get All Unpaid Invoices (and Sent/Overdue for complete picture)
                const q = query(
                    collection(db, `organizations/${orgId}/invoices`),
                    where('status', 'in', ['Unpaid', 'Partial', 'Past Due', 'Sent', 'Overdue'])
                );
                const snapshot = await getDocs(q);
                
                // 2. Group by Customer
                const customerMap = {};
                const now = new Date();
                now.setHours(0,0,0,0);

                snapshot.docs.forEach(doc => {
                    const inv = doc.data();
                    const cid = inv.customerId;
                    if (!cid) return;

                    if (!customerMap[cid]) {
                        customerMap[cid] = {
                            customerId: cid,
                            name: inv.customerName || inv.customerDetails?.name || 'Unknown',
                            email: inv.customerEmail || inv.customerDetails?.email,
                            totalDue: 0,
                            overdueAmount: 0,
                            invoiceCount: 0,
                            oldestInvoice: null,
                            invoices: [] 
                        };
                    }

                    // acctstmt-5b3e9f14 (Seam 10, part 2b): authoritative balance -- the
                    // SAME calc as the emailed statement (client twin of the server's
                    // computeInvoiceBalance; see src/utils/invoiceBalance.js). Clamped to
                    // >= 0 for the collections display. Replaces the old
                    // `balanceDue ?? total` idiom, which omitted `- amountPaid` and
                    // overstated what a partly-paid customer owed. This figure feeds the
                    // on-screen totals AND the CSV export (they read it), so both now agree
                    // with the statement.
                    const balance = Math.max(0, computeInvoiceBalance(inv).balanceDue);
                    
                    // Determine Overdue Status
                    const dueDate = inv.dueDate?.toDate ? inv.dueDate.toDate() : null;
                    const isOverdue = inv.status === 'Past Due' || inv.status === 'Overdue' || (dueDate && dueDate < now);

                    // Add to Aggregates
                    customerMap[cid].totalDue += balance;
                    customerMap[cid].invoiceCount++;
                    if (isOverdue) {
                        customerMap[cid].overdueAmount += balance;
                    }

                    // Track Oldest Date
                    if (dueDate) {
                        if (!customerMap[cid].oldestInvoice || dueDate < customerMap[cid].oldestInvoice) {
                            customerMap[cid].oldestInvoice = dueDate;
                        }
                    }

                    // Store Invoice Data for Modal
                    customerMap[cid].invoices.push({
                        id: doc.id,
                        invoiceNumber: inv.invoiceNumber,
                        dueDate: dueDate,
                        total: Number(inv.total),
                        balance: balance,
                        isOverdue: isOverdue
                    });
                });

                // 3. Convert to Array & Sort by Overdue Amount (Desc)
                const accountList = Object.values(customerMap)
                    .filter(acc => acc.totalDue > 0)
                    .sort((a, b) => b.overdueAmount - a.overdueAmount);

                setAccounts(accountList);
            } catch (error) {
                console.error("Error loading report:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [currentUserProfile]);

    const handleSendReminder = async (account, type, range) => {
        if (!account.email) return alert("No email address for this customer.");
        // The period statement (standard) can carry a date range; the urgent overdue
        // reminder ignores it. Row-level quick-send passes no range -> server defaults
        // to the last 12 months.
        const hasRange = type === 'standard' && range?.startDate && range?.endDate;
        const rangeMsg = hasRange ? ` for ${range.startDate} to ${range.endDate}` : '';
        if (!window.confirm(`Email ${type === 'overdue' ? 'URGENT overdue reminder' : `account statement${rangeMsg}`} to ${account.name}?`)) return;

        setSendingId(account.customerId);
        try {
            const sendFn = httpsCallable(functions, 'sendStatementEmail');
            const payload = { customerId: account.customerId, type };
            if (hasRange) {
                payload.startDate = range.startDate;
                payload.endDate = range.endDate;
            }
            await sendFn(payload);
            alert("Email sent successfully!");
        } catch (error) {
            console.error(error);
            alert("Failed to send: " + error.message);
        } finally {
            setSendingId(null);
        }
    };

    const handleExport = () => {
        if (accounts.length === 0) return alert("No data to export.");
        const csvData = accounts.map(acc => ({
            Customer: acc.name,
            Email: acc.email || 'N/A',
            Total_Outstanding: acc.totalDue.toFixed(2),
            Overdue_Amount: acc.overdueAmount.toFixed(2),
            Oldest_Invoice: acc.oldestInvoice ? acc.oldestInvoice.toLocaleDateString() : 'N/A'
        }));
        exportToCSV(csvData, `Collections_Report_${new Date().toISOString().split('T')[0]}.csv`);
    };

    if (loading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-blue-600" /></div>;

    return (
        <div className="p-6 max-w-6xl mx-auto">
            {/* HEADER */}
            <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
                <div className="flex items-center gap-4">
                    <Link to="/reports" className="p-2 bg-white border rounded-full hover:bg-gray-100 transition-colors">
                        <ArrowLeft size={20} className="text-gray-600" />
                    </Link>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">Collections Dashboard</h1>
                        <p className="text-gray-500 text-sm">
                            <span className="font-bold text-red-600">{accounts.filter(a => a.overdueAmount > 0).length} customers</span> with overdue balances.
                        </p>
                    </div>
                </div>

                <button 
                    onClick={handleExport}
                    className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 transition-colors shadow-sm"
                >
                    <Download size={18} /> Export List
                </button>
            </div>

            <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
                <table className="w-full text-sm text-left text-gray-500">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50 border-b">
                        <tr>
                            <th className="px-6 py-4">Customer</th>
                            <th className="px-6 py-4 text-right">Total Outstanding</th>
                            <th className="px-6 py-4 text-right">Overdue Amount</th>
                            <th className="px-6 py-4 text-center">Oldest Invoice</th>
                            <th className="px-6 py-4 text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {accounts.length === 0 ? (
                            <tr><td colSpan="5" className="p-8 text-center text-gray-500">Good news! No outstanding accounts found.</td></tr>
                        ) : (
                            accounts.map(acc => (
                                <tr 
                                    key={acc.customerId} 
                                    onClick={() => setSelectedAccount(acc)} 
                                    className="bg-white border-b hover:bg-blue-50 transition-colors cursor-pointer group"
                                >
                                    <td className="px-6 py-4">
                                        <p className="font-bold text-gray-900 group-hover:text-blue-700">{acc.name}</p>
                                        <p className="text-xs text-gray-500">{acc.invoiceCount} invoices open</p>
                                    </td>
                                    <td className="px-6 py-4 text-right font-medium text-gray-900">
                                        {formatCurrency(acc.totalDue)}
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        {acc.overdueAmount > 0 ? (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                {formatCurrency(acc.overdueAmount)}
                                            </span>
                                        ) : (
                                            <span className="text-green-600 flex items-center justify-end gap-1">
                                                <CheckCircle size={14}/> Current
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        {acc.oldestInvoice ? acc.oldestInvoice.toLocaleDateString() : 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 flex justify-center gap-2" onClick={(e) => e.stopPropagation()}>
                                        <button 
                                            onClick={() => handleSendReminder(acc, 'standard')}
                                            disabled={sendingId === acc.customerId}
                                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-md border border-gray-200"
                                            title="Send Statement"
                                        >
                                            <Send size={16} />
                                        </button>
                                        {acc.overdueAmount > 0 && (
                                            <button 
                                                onClick={() => handleSendReminder(acc, 'overdue')}
                                                disabled={sendingId === acc.customerId}
                                                className="p-2 text-white bg-red-600 hover:bg-red-700 rounded-md shadow-sm"
                                                title="Send Urgent Overdue Reminder"
                                            >
                                                {sendingId === acc.customerId ? <Loader2 size={16} className="animate-spin" /> : <MailWarning size={16} />}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* DETAIL MODAL */}
            {selectedAccount && (
                <CollectionsDetailModal 
                    account={selectedAccount}
                    onClose={() => setSelectedAccount(null)}
                    onSendReminder={handleSendReminder}
                    sendingId={sendingId}
                />
            )}
        </div>
    );
};

export default CollectionsReportView;